// apps/web/src/layouts/components/useSidebarBadgeCounts.ts
//
// Ponto único onde as contagens dos badges da sidebar admin são buscadas.
// Fica no topo de <SidebarAdminSubGroups> porque o número precisa existir fora
// do badge: um ancestral colapsado exibe a SOMA dos descendentes, e essa soma
// atravessa badges de origens diferentes (ex.: "Operação" = fila de aprovação +
// fila do analista).
//
// Cada contagem é gated pela permissão do item correspondente — sem permissão a
// query não monta e o número é 0, então o ancestral nunca soma o que o usuário
// não pode ver. `useSocketEvent` no-op com evento vazio, logo a permissão também
// decide se a assinatura (e o socket compartilhado) chega a ser adquirida.

import { useQueryClient } from '@tanstack/react-query';

import {
  pricingAdjustmentsKeys,
  pricingAdminKeys,
  pricingChangeSetKeys,
  queryKeys,
  quotationExceptionKeys,
  supportTicketsKeys,
  useAcceptanceRulesAdmin,
  useAdjustments,
  useAnalysisQueue,
  useApprovalStats,
  useChangeSetsList,
  useCommissionAttentionCount,
  useCompetitorOfferQueueCount,
  useDeductiblesCount,
  useEndorsementQueueCount,
  useMyAnalystProfile,
  usePaymentIncidentOpenCount,
  useReactivationBillingOpenCount,
  usePricingPermissions,
  useQuotationExceptions,
  useSocketEvent,
  useSupportTicketsSummary,
  useVehicleQueueCount,
  vehicleCatalogKeys,
} from '@alamo/api';
import { usePermissions } from '@alamo/auth';

import type { SidebarBadgeCounts } from './sidebarBadgeTree';

/** Rede de segurança do push realtime das aprovações de pricing. */
const PRICING_APPROVALS_REFETCH_MS = 300_000;

export function useSidebarBadgeCounts(): SidebarBadgeCounts {
  const { hasPermission } = usePermissions();
  const { canApprove, canPublish } = usePricingPermissions();
  const queryClient = useQueryClient();

  const canReadPricingApprovals = canApprove || canPublish;
  const canReadApprovalQueue = hasPermission('admin_registrations', 'read');
  const canReadAnalystQueue = hasPermission('proposals', 'analysis_read');
  const canReadCommissionAttention = hasPermission('commissions', 'manage');
  const canReadCompetitorOfferQueue = hasPermission('competitor-offers', 'view-queue');
  const canReadEndorsementQueue = hasPermission('policies', 'admin');
  const canReadPaymentIncidents = hasPermission('payment_incidents', 'read');
  const canReadReactivationBilling = hasPermission('reactivation_billing', 'read');
  const canReadQuotationExceptionQueue = hasPermission('quotation_exception', 'read_all');
  const canReadSupport = hasPermission('support', 'manage');
  const canReadVehicleQueue = hasPermission('vehicle-catalog', 'read');

  // Change-sets é a contagem que o evento dirige; as 3 entry-direct (Pilar 3 + 4)
  // entram para o total reconvergir. Os keys de deductibles/acceptance carregam
  // objeto de filtro, então invalidamos o prefixo do domínio.
  useSocketEvent(canReadPricingApprovals ? 'pricing.approvals.count_changed' : '', () => {
    void queryClient.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    void queryClient.invalidateQueries({ queryKey: pricingAdjustmentsKeys.all });
    void queryClient.invalidateQueries({ queryKey: [...pricingAdminKeys.all, 'deductibles'] });
    void queryClient.invalidateQueries({ queryKey: [...pricingAdminKeys.all, 'acceptance-rules'] });
  });
  useSocketEvent(canReadApprovalQueue ? 'registration.status.changed' : '', () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.consultantApproval.all });
  });
  const invalidateAnalystQueue = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.analystQueue.all });
  };
  useSocketEvent(canReadAnalystQueue ? 'proposal.assigned' : '', invalidateAnalystQueue);
  useSocketEvent(canReadAnalystQueue ? 'proposal.released' : '', invalidateAnalystQueue);
  useSocketEvent(canReadAnalystQueue ? 'proposal.status_changed' : '', invalidateAnalystQueue);
  useSocketEvent(canReadCommissionAttention ? 'commissions.attention.count_changed' : '', () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.commissionAttention.count() });
  });
  useSocketEvent(canReadSupport ? 'support.tickets.count_changed' : '', () => {
    void queryClient.invalidateQueries({ queryKey: supportTicketsKeys.summary() });
  });
  useSocketEvent(canReadVehicleQueue ? 'vehicle-catalog.queue.count_changed' : '', () => {
    void queryClient.invalidateQueries({ queryKey: vehicleCatalogKeys.queueCount() });
  });
  useSocketEvent(canReadQuotationExceptionQueue ? 'notifications.created' : '', () => {
    void queryClient.invalidateQueries({ queryKey: quotationExceptionKeys.all });
  });
  useSocketEvent(canReadPaymentIncidents ? 'payment-incidents.changed' : '', () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.paymentIncidents.all });
  });
  useSocketEvent(canReadReactivationBilling ? 'reactivation-billing.changed' : '', () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.reactivationBilling.all });
  });

  // Aprovações de pricing = Change Sets em approval_pending cross-pilar MAIS os
  // entries-direct em approval_pending que não geram CS: pricing_adjustment
  // (Pilar 3) + deductible + acceptance_rule (Pilar 4). CS e entry-direct são
  // disjuntos (CS ⇒ entity published; entry ⇒ entity approval_pending sem CS),
  // então a soma não duplica.
  const changeSetsQuery = useChangeSetsList(
    { status: ['approval_pending'], limit: 1 },
    {
      enabled: canReadPricingApprovals,
      refetchInterval: PRICING_APPROVALS_REFETCH_MS,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  );
  const adjustmentsQuery = useAdjustments(
    { pageSize: 1, status: 'approval_pending' },
    { enabled: canReadPricingApprovals },
  );
  const deductiblesQuery = useDeductiblesCount(
    { status: 'approval_pending' },
    { enabled: canReadPricingApprovals },
  );
  const acceptanceQuery = useAcceptanceRulesAdmin(
    { limit: 1, status: 'approval_pending' },
    { enabled: canReadPricingApprovals },
  );

  const approvalStatsQuery = useApprovalStats({ enabled: canReadApprovalQueue });

  // A fila do analista conta coisas diferentes conforme o usuário: quem tem
  // perfil de analista vê as propostas atribuídas a si e ainda não iniciadas;
  // quem não tem vê as propostas sem dono. O perfil precisa ter respondido
  // antes de a fila ser consultada, senão o segmento sai errado.
  const analystProfileQuery = useMyAnalystProfile({ enabled: canReadAnalystQueue });
  const isAnalyst = Boolean(analystProfileQuery.data);
  const analystQueueQuery = useAnalysisQueue(
    isAnalyst
      ? { segment: 'mine', assignmentState: 'assigned_not_started', limit: 1 }
      : { segment: 'unclaimed', limit: 1 },
    { enabled: canReadAnalystQueue && analystProfileQuery.isSuccess },
  );

  const commissionQuery = useCommissionAttentionCount({ enabled: canReadCommissionAttention });
  // Álamo Cobre soma `pending` + `under_analysis` — os dois status com SLA
  // correndo do lado do analista. A fila não emite evento de socket, então o
  // número acompanha o refetch periódico da própria query.
  const competitorOfferQuery = useCompetitorOfferQueueCount({
    enabled: canReadCompetitorOfferQueue,
  });
  const endorsementQueueQuery = useEndorsementQueueCount({
    enabled: canReadEndorsementQueue,
  });
  const paymentIncidentQuery = usePaymentIncidentOpenCount({
    enabled: canReadPaymentIncidents,
    refetchInterval: 300_000,
  });
  const reactivationBillingQuery = useReactivationBillingOpenCount({
    enabled: canReadReactivationBilling,
    refetchInterval: 300_000,
  });
  const quotationExceptionQueueQuery = useQuotationExceptions(
    { scope: 'work_queue', page: 1, limit: 1 },
    {
      admin: true,
      enabled: canReadQuotationExceptionQueue,
      refetchInterval: 60_000,
    },
  );
  const supportQuery = useSupportTicketsSummary({ enabled: canReadSupport });
  const vehicleQueueQuery = useVehicleQueueCount({ enabled: canReadVehicleQueue });

  return {
    approvals: canReadPricingApprovals
      ? (changeSetsQuery.data?.count ?? 0) +
        (adjustmentsQuery.data?.meta.total ?? 0) +
        (deductiblesQuery.data ?? 0) +
        (acceptanceQuery.data?.total ?? 0)
      : 0,
    approvalQueue: canReadApprovalQueue
      ? (approvalStatsQuery.data?.statuses.find((item) => item.status === 'pending')?.count ?? 0)
      : 0,
    analystQueue: canReadAnalystQueue ? (analystQueueQuery.data?.count ?? 0) : 0,
    commissionAttention: canReadCommissionAttention ? (commissionQuery.data?.total ?? 0) : 0,
    competitorOfferQueue: canReadCompetitorOfferQueue ? (competitorOfferQuery.data ?? 0) : 0,
    endorsementQueue: canReadEndorsementQueue ? (endorsementQueueQuery.data ?? 0) : 0,
    paymentIncidentOpen: canReadPaymentIncidents ? (paymentIncidentQuery.data?.count ?? 0) : 0,
    reactivationBillingOpen: canReadReactivationBilling
      ? (reactivationBillingQuery.data?.count ?? 0)
      : 0,
    quotationExceptionQueue: canReadQuotationExceptionQueue
      ? (quotationExceptionQueueQuery.data?.summary.awaitingAssignment ?? 0)
      : 0,
    support: canReadSupport ? (supportQuery.data?.open ?? 0) : 0,
    vehicleQueue: canReadVehicleQueue ? (vehicleQueueQuery.data?.count ?? 0) : 0,
  };
}
